"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const antd_1 = require("antd");
const react_query_1 = require("@tanstack/react-query");
const react_router_dom_1 = require("react-router-dom");
const FormLayoutContainer_1 = __importDefault(require("../common/FormLayoutContainer"));
const useMutateWrapper_1 = require("../../hooks/useMutateWrapper");
const tenants_1 = require("../../services/tenants");
const locales_1 = require("../../services/locales");
const validationMessages_1 = __importDefault(require("../../constants/validationMessages"));
const Icon_1 = __importDefault(require("../common/Icon"));
const queryKeys_1 = __importDefault(require("../../constants/queryKeys"));
const { Item, useForm } = antd_1.Form;
const { Option } = antd_1.Select;
const { Group } = antd_1.Radio;
const ManageTenant = () => {
    const [manageTenantForm] = useForm();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { id: tenantId } = (0, react_router_dom_1.useParams)();
    const [defaultLocaleCode, setDefaultLocaleCode] = (0, react_1.useState)('Add Language');
    const [locales, setLocales] = (0, react_1.useState)([]);
    const [localeTableData, setLocaleTableData] = (0, react_1.useState)([]);
    const { isLoading, data } = (0, react_query_1.useQuery)([queryKeys_1.default.TENANTS.ALL, tenantId], (0, tenants_1.getTenants)(tenantId), {
        onError: (err) => {
            const { status, data: { msg } } = err.response;
            switch (status) {
                case 404: return navigate('/tenants/404', { state: { comingFrom: 'manage' } });
                case 500: return navigate('/tenants/500');
                default: antd_1.message.error(msg);
            }
        },
    });
    (0, react_1.useEffect)(() => {
        if (data) {
            manageTenantForm.setFieldsValue({
                tenantName: data.tenantName,
                tenantId: data.tenantId,
                isActive: data.isActive,
            });
            setDefaultLocaleCode(data.defaultLocaleCode);
            if (data.locales) {
                setLocales(data.locales.map(({ code }) => code));
                setLocaleTableData(data.locales);
            }
        }
    }, [data]);
    const { data: localesData } = (0, react_query_1.useQuery)([queryKeys_1.default.TENANTS.LOCALES], (0, locales_1.getLocales)());
    // generating options for locales dropdown and removing options once selected
    const filteredOptions = localesData === null || localesData === void 0 ? void 0 : localesData.filter(({ code }) => !locales.includes(code)).map(({ localeId, code, localeName }) => ((0, jsx_runtime_1.jsx)(Option, Object.assign({ value: code }, { children: localeName }), localeId)));
    // setting defaultLocaleCode and adding it to locales array and creating localeTableData
    const handleLocaleChange = (value) => setLocales([...locales, value]);
    // handleLocaleChange func triggers this effect and add/remove rows to localeTableData
    (0, react_1.useEffect)(() => {
        if ((localesData === null || localesData === void 0 ? void 0 : localesData.length) > 0 && localeTableData.length > 0) {
            const newLocaleTableData = locales.map((lTA) => {
                const { localeId, localeName, code } = localesData
                    && localesData.find((lD) => lD.code === lTA);
                const { isActive } = localeTableData.find((lTD) => lTD.code === lTA) || { isActive: true };
                return { localeId, localeName, code, isActive };
            });
            setLocaleTableData(newLocaleTableData);
            manageTenantForm.setFieldsValue({ defaultLocaleCode: 'Add Language' });
        }
    }, [locales, localesData]);
    // func to add/remove active locales
    const handleCheckboxChange = (e) => {
        const { checked, value } = e.target;
        value !== defaultLocaleCode && (setLocaleTableData(localeTableData.map((lTD) => (lTD.code === value
            ? Object.assign(Object.assign({}, lTD), { isActive: checked }) : lTD))));
    };
    // func to toggle default locale
    const handleRadioChange = (e) => {
        const { value } = e.target;
        setDefaultLocaleCode(value);
        setLocaleTableData(localeTableData.map((lTD) => (lTD.code === value
            ? Object.assign(Object.assign({}, lTD), { isActive: true }) : lTD)));
    };
    // remove rows/locales from localeTableData
    const handleLocaleRemove = (code) => {
        setLocales(locales.filter((loc) => loc !== code));
        setLocaleTableData(localeTableData.filter((lTD) => lTD.code !== code));
    };
    const columns = [
        { title: 'Name', dataIndex: 'localeName', key: 'localeName' },
        { title: 'Code', dataIndex: 'code', key: 'code' },
        { title: 'Active', dataIndex: 'isActive', key: 'isActive', render: (_text, { code }) => {
                var _a;
                return ((0, jsx_runtime_1.jsx)(antd_1.Checkbox, { checked: code === defaultLocaleCode
                        || ((_a = localeTableData.find((lTD) => lTD.code === code)) === null || _a === void 0 ? void 0 : _a.isActive), value: code, onChange: handleCheckboxChange }));
            } },
        { title: 'Default', dataIndex: 'isDefault', key: 'isDefault', render: (_text, { code }) => ((0, jsx_runtime_1.jsx)(Group, Object.assign({ value: defaultLocaleCode, onChange: handleRadioChange }, { children: (0, jsx_runtime_1.jsx)(antd_1.Radio, { value: code }) }))) },
        { title: '', dataIndex: '', key: 'x', render: (_text, { code }) => ((0, jsx_runtime_1.jsx)(antd_1.Button, { icon: (0, jsx_runtime_1.jsx)(Icon_1.default, { iconName: 'delete', variant: 'danger' }), style: { paddingTop: '0.25rem' }, disabled: code === defaultLocaleCode, onClick: () => handleLocaleRemove(code) })) },
    ];
    const { isLoading: isSubmitting, onFormSubmit } = (0, useMutateWrapper_1.useMutateWrapper)(queryKeys_1.default.TENANTS.ALL, tenants_1.updateTenant, { navigateTo: '/tenants' });
    const handleSubmit = (values) => {
        const modifiedLocales = localeTableData.map(({ code, isActive }) => ({ code, isActive }));
        onFormSubmit({ locales: modifiedLocales, defaultLocaleCode })(values);
    };
    return ((0, jsx_runtime_1.jsx)(FormLayoutContainer_1.default, Object.assign({ title: data === null || data === void 0 ? void 0 : data.tenantName, loading: isLoading || isSubmitting }, { children: (0, jsx_runtime_1.jsxs)(antd_1.Form, Object.assign({ scrollToFirstError: true, layout: 'vertical', form: manageTenantForm, name: 'manage-tenant-form', onFinish: handleSubmit }, { children: [(0, jsx_runtime_1.jsx)(Item, Object.assign({ name: 'tenantName', label: 'Tenant Name', rules: [{ required: true, message: validationMessages_1.default.required }] }, { children: (0, jsx_runtime_1.jsx)(antd_1.Input, { showCount: true, maxLength: 250 }) })), (0, jsx_runtime_1.jsx)(Item, Object.assign({ name: 'tenantId', label: 'Tenant ID' }, { children: (0, jsx_runtime_1.jsx)(antd_1.Input, { disabled: true }) })), (0, jsx_runtime_1.jsx)(Item, Object.assign({ name: 'isActive', valuePropName: 'checked', initialValue: false }, { children: (0, jsx_runtime_1.jsx)(antd_1.Checkbox, { children: "Active?" }) })), (0, jsx_runtime_1.jsx)(Item, Object.assign({ name: 'defaultLocaleCode', label: 'Languages', rules: [{
                            validator: () => (localeTableData.length > 0
                                ? Promise.resolve()
                                : Promise.reject(validationMessages_1.default.required)),
                        }] }, { children: (0, jsx_runtime_1.jsx)(antd_1.Select, Object.assign({ disabled: (localesData === null || localesData === void 0 ? void 0 : localesData.length) === locales.length, value: defaultLocaleCode, onChange: handleLocaleChange }, { children: filteredOptions })) })), localeTableData.length > 0 && ((0, jsx_runtime_1.jsx)(Item, Object.assign({ label: 'Selected Languages' }, { children: (0, jsx_runtime_1.jsx)(antd_1.Table, { size: 'small', columns: columns, dataSource: localeTableData, pagination: false, rowKey: ({ localeId }) => localeId }) }))), (0, jsx_runtime_1.jsx)(antd_1.Button, Object.assign({ type: 'primary', htmlType: 'submit' }, { children: "Update" }))] })) })));
};
exports.default = ManageTenant;
